/**
 * React Query Hook: useCPMBenchmarks
 *
 * Fetch CPM benchmarks from the database with optional filtering.
 * Provides automatic caching, request deduplication, and loading states.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';

export interface CPMBenchmark {
  id: string;
  platform: string;
  metric_type: 'impressions' | 'followers' | 'engagements';
  country_code: string;
  industry: string;
  original_currency: 'USD' | 'EUR' | 'DKK';
  original_value: number;
  original_min?: number;
  original_max?: number;
  source: string;
  source_url?: string;
  data_period: string;
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CPMBenchmarksResponse {
  benchmarks: CPMBenchmark[];
  count: number;
}

export interface UseCPMBenchmarksFilters {
  platform?: string;
  country_code?: string;
  metric_type?: 'impressions' | 'followers' | 'engagements';
  industry?: string;
  active?: boolean;
  [key: string]: string | boolean | undefined; // Index signature for query key compatibility
}

/**
 * Fetch CPM benchmarks from the database
 *
 * @param filters - Optional filters for platform, country, metric type, industry
 * @returns React Query result with benchmarks data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCPMBenchmarks({
 *   platform: 'facebook',
 *   country_code: 'DK',
 *   metric_type: 'impressions',
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage />;
 *
 * return (
 *   <ul>
 *     {data.benchmarks.map(benchmark => (
 *       <li key={benchmark.id}>{benchmark.platform}: ${benchmark.original_value}</li>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useCPMBenchmarks(filters: UseCPMBenchmarksFilters = {}) {
  return useQuery({
    queryKey: queryKeys.cpmBenchmarks.list(
      filters as Record<string, string | number | boolean | Record<string, unknown>>
    ),
    queryFn: async () => {
      // Build query string from filters
      const params = new URLSearchParams();

      if (filters.platform) {
        params.append('platform', filters.platform);
      }

      if (filters.country_code) {
        params.append('country_code', filters.country_code);
      }

      if (filters.metric_type) {
        params.append('metric_type', filters.metric_type);
      }

      if (filters.industry !== undefined) {
        params.append('industry', filters.industry);
      }

      if (filters.active !== undefined) {
        params.append('active', String(filters.active));
      }

      const queryString = params.toString();
      const url = `/api/cpm-benchmarks${queryString ? `?${queryString}` : ''}`;

      const response = await fetchWithBypass(url);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch CPM benchmarks');
      }

      return response.json() as Promise<CPMBenchmarksResponse>;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (CPM benchmarks don't change frequently)
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });
}

/**
 * Fetch all active CPM benchmarks (no filters)
 *
 * Convenience hook for fetching all active benchmarks without filters.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useAllCPMBenchmarks();
 *
 * return (
 *   <table>
 *     {data?.benchmarks.map(benchmark => (
 *       <tr key={benchmark.id}>
 *         <td>{benchmark.platform}</td>
 *         <td>{benchmark.country_code}</td>
 *         <td>${benchmark.original_value}</td>
 *       </tr>
 *     ))}
 *   </table>
 * );
 * ```
 */
export function useAllCPMBenchmarks() {
  return useCPMBenchmarks({ active: true });
}
